import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { StoryCard } from "../components/StoryUI";
import { useApp } from "../lib/ctx";

export const Search = () => {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [cats, setCats] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);
  useEffect(() => {
    if (!q) { setResults([]); return; }
    setLoading(true);
    api.get("/stories", { params: { q, limit: 50 } }).then(r => { setResults(r.data); setLoading(false); });
  }, [q]);
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="search-page">
      <h1 className="font-display font-extrabold text-3xl sm:text-5xl">Search</h1>
      <input autoFocus value={q} onChange={e => { setQ(e.target.value); setParams({ q: e.target.value }); }}
        data-testid="search-input" placeholder="Try: Metro, DSEX, Tigers…"
        className="w-full mt-5 h-14 px-5 rounded-full bg-card border border-border text-base outline-none focus:border-signal" />
      {loading && <div className="text-center py-10 text-muted-foreground">Searching…</div>}
      {!loading && q && results.length === 0 && <div className="text-center py-10 text-muted-foreground">No stories match "{q}".</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
        {results.map(s => <StoryCard key={s.id} story={s} cats={cats} />)}
      </div>
    </div>
  );
};

export const Explainers = () => {
  const [stories, setStories] = useState([]);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/stories", { params: { explainer: true, limit: 50 } }).then(r => r.data),
      api.get("/categories").then(r => r.data),
    ]).then(([s, c]) => { setStories(s); setCats(c); });
  }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="explainers-page">
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl">Newsgram Explains</h1>
      <p className="text-muted-foreground mt-2 max-w-xl">Deeper, calmer, still short. We unpack the news so you don't need five tabs.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {stories.map(s => <StoryCard key={s.id} story={s} cats={cats} />)}
      </div>
    </div>
  );
};

export const SixtySeconds = () => {
  const [stories, setStories] = useState([]);
  const [cats, setCats] = useState([]);
  useEffect(() => {
    Promise.all([
      api.get("/stories", { params: { in_60_seconds: true, limit: 20 } }).then(r => r.data),
      api.get("/categories").then(r => r.data),
    ]).then(([s, c]) => { setStories(s); setCats(c); });
  }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="sixty-page">
      <div className="text-xs font-black uppercase tracking-widest text-signal">Today in 60 Seconds</div>
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl mt-2">Catch up fast.</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {stories.map(s => <StoryCard key={s.id} story={s} cats={cats} />)}
      </div>
    </div>
  );
};

export const Categories = () => {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="categories-page">
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl">Categories</h1>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-8">
        {cats.map(c => (
          <Link key={c.id} to={`/category/${c.slug}`} data-testid={`cat-link-${c.slug}`}
            className="rounded-2xl p-6 text-white font-display font-extrabold text-2xl card-soft hover:scale-[1.02] transition-transform"
            style={{ backgroundColor: c.color }}>{c.name}</Link>
        ))}
      </div>
    </div>
  );
};

export const Saved = () => {
  const { saved } = useApp();
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="saved-page">
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl">Saved stories</h1>
      <p className="text-muted-foreground mt-2">Stored on this device.</p>
      {saved.length === 0 ? (
        <div className="mt-10 text-center text-muted-foreground">No saved stories yet. Tap the bookmark icon on any story.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {saved.map(s => <StoryCard key={s.id} story={s} cats={cats} />)}
        </div>
      )}
    </div>
  );
};

export const Videos = () => {
  const [videos, setVideos] = useState([]);
  useEffect(() => { api.get("/videos").then(r => setVideos(r.data)); }, []);
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="videos-page">
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl">Video hub</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {videos.map(v => (
          <div key={v.id} className="rounded-2xl overflow-hidden bg-card card-soft" data-testid={`video-${v.id}`}>
            <div className="aspect-video">
              <iframe src={v.url} title={v.title} className="w-full h-full" allowFullScreen />
            </div>
            <div className="p-4 font-display font-bold">{v.title}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const LiveBlogList = () => {
  const [blogs, setBlogs] = useState([]);
  useEffect(() => { api.get("/live-blogs").then(r => setBlogs(r.data)); }, []);
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="liveblogs-page">
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl">Live blogs</h1>
      <div className="mt-8 space-y-3">
        {blogs.map(b => (
          <Link to={`/live/${b.slug}`} key={b.id} data-testid={`liveblog-${b.slug}`}
            className="block rounded-2xl bg-card border border-border p-5 hover:border-signal">
            <div className="text-[11px] font-black uppercase tracking-widest text-signal inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />LIVE</div>
            <h3 className="font-display font-extrabold text-xl mt-1">{b.title}</h3>
            <p className="text-sm text-muted-foreground">{b.summary}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const LiveBlogDetail = () => {
  const { slug } = useParams();
  const [lb, setLb] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/live-blogs/${slug}`).then(r => setLb(r.data));
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [slug]);
  if (!lb) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  const pinned = lb.updates.find(u => u.pinned);
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="liveblog-detail">
      <div className="text-[11px] font-black uppercase tracking-widest text-signal inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />LIVE</div>
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-2">{lb.title}</h1>
      {pinned && <div className="mt-5 rounded-2xl border-l-4 border-signal pl-4 py-3 bg-signal/5"><div className="text-[11px] font-bold uppercase text-signal">Pinned</div><p>{pinned.text}</p></div>}
      <div className="mt-8 space-y-5">
        {lb.updates.filter(u => !u.pinned).map(u => (
          <div key={u.id} className="flex gap-4" data-testid={`update-${u.id}`}>
            <div className="flex-none w-16 text-xs text-muted-foreground font-mono">{new Date(u.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
            <div className="flex-1 border-l-2 border-border pl-4 pb-3">{u.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Quiz = () => {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);
  useEffect(() => { api.get("/quizzes").then(r => setQuiz(r.data[0])); }, []);
  if (!quiz) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  const score = Object.keys(answers).filter(k => answers[k] === quiz.questions[k].correct).length;
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="quiz-page">
      <div className="text-xs font-black uppercase tracking-widest text-signal">Weekly News Quiz</div>
      <h1 className="font-display font-extrabold text-3xl sm:text-5xl mt-2">{quiz.title}</h1>
      <div className="mt-8 space-y-6">
        {quiz.questions.map((q, qi) => (
          <div key={qi} className="rounded-2xl border border-border p-5 bg-card">
            <div className="font-bold">{qi+1}. {q.q}</div>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {q.options.map((o, oi) => {
                const sel = answers[qi] === oi;
                const correct = done && oi === q.correct;
                const wrong = done && sel && oi !== q.correct;
                return (
                  <button key={oi} onClick={() => !done && setAnswers(a => ({ ...a, [qi]: oi }))} data-testid={`q${qi}-opt${oi}`}
                    className={`text-left p-3 rounded-xl border text-sm font-semibold ${correct ? "bg-emerald-500/10 border-emerald-500" : wrong ? "bg-signal/10 border-signal" : sel ? "bg-foreground text-background border-foreground" : "border-border bg-background"}`}>
                    {o}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      {!done ? (
        <button onClick={() => setDone(true)} disabled={Object.keys(answers).length < quiz.questions.length}
          data-testid="quiz-submit" className="mt-6 h-12 px-8 rounded-full bg-signal text-white font-bold disabled:opacity-50">Submit</button>
      ) : (
        <div className="mt-6 rounded-2xl bg-foreground text-background p-6 text-center" data-testid="quiz-result">
          <div className="font-display font-extrabold text-4xl">{score} / {quiz.questions.length}</div>
          <div className="text-sm opacity-70 mt-1">Share your score #NewsgramQuiz</div>
        </div>
      )}
    </div>
  );
};

export const Newsletter = () => {
  const [email, setEmail] = useState("");
  const [lang, setLang] = useState("en");
  const [cats, setCats] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/categories").then(r => setCats(r.data)); }, []);
  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try { await api.post("/newsletter/subscribe", { email, language: lang, categories: selectedCats }); setStatus("ok"); }
    catch { setStatus("err"); }
  };
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="newsletter-page">
      <div className="text-xs font-black uppercase tracking-widest text-signal">The Morning Gram</div>
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl mt-2">Daily 60-second briefing.</h1>
      <p className="text-muted-foreground mt-3">Six short stories, 8 AM Dhaka time. Pick your language and the topics you care about.</p>
      <form onSubmit={submit} className="mt-8 space-y-5">
        <input required type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
          data-testid="nl-email" className="w-full h-12 px-5 rounded-full bg-card border border-border outline-none focus:border-signal" />
        <div>
          <div className="text-sm font-bold mb-2">Language</div>
          <div className="flex gap-2">
            {["en","bn"].map(l => (
              <button key={l} type="button" onClick={() => setLang(l)} data-testid={`nl-lang-${l}`}
                className={`h-10 px-4 rounded-full text-sm font-bold border ${lang===l ? "bg-foreground text-background border-foreground" : "border-border"}`}>{l==="en"?"English":"বাংলা"}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm font-bold mb-2">Topics</div>
          <div className="flex flex-wrap gap-2">
            {cats.map(c => {
              const on = selectedCats.includes(c.slug);
              return (
                <button key={c.id} type="button" onClick={() => setSelectedCats(s => on ? s.filter(x=>x!==c.slug) : [...s, c.slug])}
                  data-testid={`nl-cat-${c.slug}`}
                  className={`h-9 px-3 rounded-full text-xs font-bold border ${on ? "text-white border-transparent" : "border-border text-foreground"}`}
                  style={on ? { backgroundColor: c.color } : {}}>{c.name}</button>
              );
            })}
          </div>
        </div>
        <button data-testid="nl-submit" className="h-12 px-8 rounded-full bg-signal text-white font-bold">
          {status === "loading" ? "Subscribing…" : "Subscribe"}
        </button>
        {status === "ok" && <p className="text-emerald-500 text-sm">You're in. First edition tomorrow at 8 AM.</p>}
        {status === "err" && <p className="text-signal text-sm">Something broke. Try again?</p>}
      </form>
    </div>
  );
};

export const Tip = () => {
  const [form, setForm] = useState({ name: "", contact: "", message: "", link: "" });
  const [status, setStatus] = useState(null);
  const submit = async (e) => { e.preventDefault(); setStatus("loading");
    try { await api.post("/tips", form); setStatus("ok"); setForm({ name: "", contact: "", message: "", link: "" }); } catch { setStatus("err"); }
  };
  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="tip-page">
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl">Submit a tip</h1>
      <p className="text-muted-foreground mt-3">We read every tip. Share details and (if safe) how we can reach you. We protect sources.</p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <input placeholder="Your name (optional)" value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))} data-testid="tip-name" className="w-full h-12 px-5 rounded-full bg-card border border-border outline-none focus:border-signal" />
        <input placeholder="Contact (email or signal)" value={form.contact} onChange={e=>setForm(f=>({...f, contact:e.target.value}))} data-testid="tip-contact" className="w-full h-12 px-5 rounded-full bg-card border border-border outline-none focus:border-signal" />
        <textarea required rows={6} placeholder="What should we look into?" value={form.message} onChange={e=>setForm(f=>({...f, message:e.target.value}))} data-testid="tip-message" className="w-full p-4 rounded-2xl bg-card border border-border outline-none focus:border-signal" />
        <input placeholder="Any link to share?" value={form.link} onChange={e=>setForm(f=>({...f, link:e.target.value}))} data-testid="tip-link" className="w-full h-12 px-5 rounded-full bg-card border border-border outline-none focus:border-signal" />
        <button data-testid="tip-submit" className="h-12 px-8 rounded-full bg-signal text-white font-bold">{status==="loading"?"Sending…":"Send tip"}</button>
        {status === "ok" && <p className="text-emerald-500 text-sm">Thank you. We'll be in touch if we need more.</p>}
      </form>
    </div>
  );
};

export const Advertise = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", budget: "", message: "" });
  const [status, setStatus] = useState(null);
  const submit = async (e) => { e.preventDefault(); setStatus("loading");
    try { await api.post("/ad-inquiries", form); setStatus("ok"); setForm({ name: "", email: "", company: "", budget: "", message: "" }); } catch { setStatus("err"); }
  };
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="advertise-page">
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl">Advertise with Newsgram</h1>
      <p className="text-muted-foreground mt-3">We reach a hard-to-find audience: Bangladesh's 18–36, mobile-first, English readers.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        {[{v:"500K+", l:"Monthly readers"},{v:"72%", l:"Mobile"},{v:"18-36", l:"Core age"},{v:"5 min", l:"Session"}].map(s => (
          <div key={s.l} className="rounded-2xl bg-card border border-border p-4"><div className="font-display font-extrabold text-2xl">{s.v}</div><div className="text-xs text-muted-foreground">{s.l}</div></div>
        ))}
      </div>
      <h2 className="font-display font-extrabold text-2xl mt-10">Formats</h2>
      <ul className="mt-3 space-y-2 text-sm">
        <li>• In-feed display cards (every 6 stories)</li>
        <li>• Sponsored stories (always labeled)</li>
        <li>• "Presented by" segments on daily newsletter</li>
        <li>• Social campaigns (Instagram, LinkedIn)</li>
      </ul>
      <form onSubmit={submit} className="mt-10 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input required placeholder="Name" value={form.name} onChange={e=>setForm(f=>({...f, name:e.target.value}))} data-testid="ad-name" className="h-12 px-5 rounded-full bg-card border border-border outline-none focus:border-signal" />
          <input required type="email" placeholder="Email" value={form.email} onChange={e=>setForm(f=>({...f, email:e.target.value}))} data-testid="ad-email" className="h-12 px-5 rounded-full bg-card border border-border outline-none focus:border-signal" />
          <input placeholder="Company" value={form.company} onChange={e=>setForm(f=>({...f, company:e.target.value}))} data-testid="ad-company" className="h-12 px-5 rounded-full bg-card border border-border outline-none focus:border-signal" />
          <input placeholder="Budget range" value={form.budget} onChange={e=>setForm(f=>({...f, budget:e.target.value}))} data-testid="ad-budget" className="h-12 px-5 rounded-full bg-card border border-border outline-none focus:border-signal" />
        </div>
        <textarea rows={5} placeholder="Tell us about your campaign" value={form.message} onChange={e=>setForm(f=>({...f, message:e.target.value}))} data-testid="ad-message" className="w-full p-4 rounded-2xl bg-card border border-border outline-none focus:border-signal" />
        <button data-testid="ad-submit" className="h-12 px-8 rounded-full bg-signal text-white font-bold">{status==="loading"?"Sending…":"Request media kit"}</button>
        {status==="ok" && <p className="text-emerald-500 text-sm">Thanks. Our team will reach out within 24 hours.</p>}
      </form>
    </div>
  );
};

export const About = () => (
  <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-10" data-testid="about-page">
    <section>
      <h1 className="font-display font-extrabold text-4xl sm:text-6xl">About Newsgram</h1>
      <p className="mt-4 text-lg">Newsgram is Bangladesh's bite-sized news brand. Short, factual, neutral — a little witty. We publish on the web, Instagram, Facebook, LinkedIn, X and WhatsApp.</p>
    </section>
    <section id="team">
      <h2 className="font-display font-extrabold text-2xl">Team</h2>
      <p className="mt-2 text-muted-foreground">A small newsroom of editors, writers and designers. We write every summary ourselves and credit the original reporting.</p>
    </section>
    <section id="standards">
      <h2 className="font-display font-extrabold text-2xl">Editorial standards & ethics</h2>
      <ul className="mt-3 list-disc pl-5 space-y-1 text-sm">
        <li>We write original summaries and always link the original publication.</li>
        <li>Images are owned, licensed, or credited.</li>
        <li>We stay politically neutral — balanced headlines, "what each side says" where relevant.</li>
        <li>Sponsored content is always labeled.</li>
      </ul>
    </section>
    <section id="corrections"><h2 className="font-display font-extrabold text-2xl">Corrections</h2><p className="mt-2 text-sm">We correct in public. Stories that are updated show an UPDATED badge with a dated note.</p></section>
    <section id="fact-check"><h2 className="font-display font-extrabold text-2xl">Fact-check policy</h2><p className="mt-2 text-sm">We verify with at least one primary source, prefer named sources, and clearly label analysis vs reporting.</p></section>
    <section id="privacy"><h2 className="font-display font-extrabold text-2xl">Privacy policy</h2><p className="mt-2 text-sm">We collect only the minimum needed to run the site. We never sell your data.</p></section>
    <section id="terms"><h2 className="font-display font-extrabold text-2xl">Terms</h2><p className="mt-2 text-sm">Use Newsgram for personal reading. Don't republish our summaries without permission.</p></section>
    <section id="contact"><h2 className="font-display font-extrabold text-2xl">Contact</h2><p className="mt-2 text-sm">Press & partnerships: hello@newsgram.bd</p></section>
  </div>
);

export const NotFound = () => (
  <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-4" data-testid="not-found">
    <div className="text-signal font-display font-black text-9xl">404</div>
    <h1 className="font-display font-extrabold text-3xl mt-2">We looked. It isn't here.</h1>
    <p className="text-muted-foreground mt-2">Maybe the story moved, or we're yet to publish it.</p>
    <Link to="/" className="mt-6 h-11 px-6 rounded-full bg-foreground text-background font-bold inline-flex items-center">Back home →</Link>
  </div>
);
